import { identityRequest, Label } from "./physicalIdentityApi";

type BatchPage = { batchId: string; quantity: number; page: number; labels: Label[] };
export class PrinterExportError extends Error {}

/** Fetches the immutable batch, checking every page before returning any CSV. */
export async function exportIdentityPrinterCsv(batchId: string, quantity: number,
  progress: (loaded: number) => void = () => {},
  request: (path: string) => Promise<BatchPage> = path => identityRequest<BatchPage>(path)) {
  if (!batchId || !Number.isInteger(quantity) || quantity < 1 || quantity > 10000)
    throw new PrinterExportError("Load a valid batch of 1–10,000 labels first.");
  const labels: Label[] = [], tokens = new Set<string>(), serials = new Set<string>();
  let origin: string | undefined, environment: string | undefined, carrierType: string | undefined;
  for (let page = 0; page * 100 < quantity; page++) {
    const result = await request(`/batches/${encodeURIComponent(batchId)}?page=${page}`);
    if (result.batchId !== batchId || result.quantity !== quantity || result.page !== page ||
        result.labels.length !== Math.min(100, quantity - page * 100))
      throw new PrinterExportError("The batch export is incomplete or changed. Reload the batch and retry; no partial file was downloaded.");
    for (const label of result.labels) {
      let url: URL;
      try { url = new URL(label.resolverUrl); } catch { throw new PrinterExportError("A label has an invalid resolver URL. Contact HKP before printing."); }
      const env = label.environment || "LEGACY", type = label.carrierType || "QR";
      if (!/^[A-Za-z0-9_-]{22}$/.test(label.manualCode) || tokens.has(label.manualCode) ||
          url.protocol !== "https:" || url.username || url.password || url.search || url.hash ||
          url.pathname !== `/q/${label.manualCode}` || (origin && origin !== url.origin) ||
          (environment && environment !== env) || (carrierType && carrierType !== type) ||
          !["QR", "NFC", "WOVEN_QR"].includes(type) ||
          (label.serial && (serials.has(label.serial) || !/^HKP-\d+$/.test(label.serial))))
        throw new PrinterExportError("Duplicate or inconsistent label data found. Contact HKP before printing; no file was downloaded.");
      tokens.add(label.manualCode); if (label.serial) serials.add(label.serial);
      origin = url.origin; environment = env; carrierType = type; labels.push(label);
    }
    progress(labels.length);
  }
  const cell = (value: string) => '"' + value.replace(/"/g, '""') + '"';
  const rows = ["batchId,serial,environment,testMark,carrierType,manualCode,resolverUrl"];
  labels.forEach(label => rows.push([batchId, label.serial || "", label.environment || "LEGACY",
    label.testMark || (label.environment && label.environment !== "PRODUCTION" ? `${label.environment} — TEST ONLY` : ""),
    label.carrierType || "QR", label.manualCode, label.resolverUrl].map(cell).join(",")));
  return { csv: "\uFEFF" + rows.join("\r\n") + "\r\n", quantity: labels.length, origin, environment, carrierType };
}
